<template>
  <div class="page-stack">
    <div class="sticky-toolbar print-controls">
      <a-card :bordered="false" class="sticky-toolbar-card">
        <a-space wrap align="center">
          <div class="toolbar-label">Дата:</div>
          <a-button @click="shiftDate(-7)"><LeftOutlined /></a-button>
          <a-date-picker v-model:value="dateValue" format="YYYY-MM-DD" :allowClear="false" inputReadOnly />
          <a-button @click="shiftDate(7)"><RightOutlined /></a-button>
          <a-button @click="load" :loading="loading">Обновить</a-button>
          <a-button type="primary" @click="printPage">Печать</a-button>
          <div class="toolbar-range">Неделя: {{ weekLabel }}</div>
        </a-space>
      </a-card>
    </div>

    <section class="print-sheet">
      <header class="print-header">
        <h1>Отчет заказчика за неделю</h1>
        <div>{{ weekLabel }}</div>
      </header>

      <div class="mobile-week-list">
        <a-card
          v-for="item in days"
          :key="`mobile-${item.date}`"
          size="small"
          :class="{ 'selected-mobile-card': item.date === selectedDate }"
        >
          <div class="mobile-day-header">
            <div class="print-date">{{ item.headerTitle }}</div>
            <div class="print-status">{{ item.exists ? label(item.order.status || 'DRAFT') : 'Нет заказа' }}</div>
          </div>

          <div class="mobile-meal-grid">
            <div v-for="meal in mealCards" :key="`mobile-${item.date}-${meal.meal}`" class="mobile-meal-card">
              <div class="mobile-meal-title">{{ meal.title }}</div>
              <div class="print-meal-summary">{{ item.meals[meal.meal] }}</div>
              <div v-if="item.children[meal.meal]?.length" class="print-children">
                <div
                  v-for="child in item.children[meal.meal]"
                  :key="`mobile-${item.date}-${meal.meal}-${child.child_id}`"
                  class="print-child"
                >
                  {{ child.full_name }}
                  <span v-if="child.tags?.length" class="print-tag-icons">
                    {{ child.tags.map(tagToPrintIcon).filter(Boolean).join(' ') }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </a-card>
      </div>

      <table class="print-table">
        <thead>
          <tr>
            <th>Дата</th>
            <th>Завтрак</th>
            <th>Обед</th>
            <th>Полдник</th>
            <th>Ужин</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="item in days" :key="item.date" :class="{ 'selected-row': item.date === selectedDate }">
            <td class="print-date-cell">
              <div class="print-date">{{ item.headerTitle }}</div>
              <div class="print-status">{{ item.exists ? label(item.order.status || 'DRAFT') : 'Нет заказа' }}</div>
            </td>

            <td v-for="meal in mealCards" :key="`${item.date}-${meal.meal}`" class="print-meal-cell">
              <div class="print-meal-summary">{{ item.meals[meal.meal] }}</div>
              <div v-if="item.children[meal.meal]?.length" class="print-children">
                <div
                  v-for="child in item.children[meal.meal]"
                  :key="`${item.date}-${meal.meal}-${child.child_id}`"
                  class="print-child"
                >
                  {{ child.full_name }}
                  <span v-if="child.tags?.length" class="print-tag-icons">
                    {{ child.tags.map(tagToPrintIcon).filter(Boolean).join(' ') }}
                  </span>
                </div>
              </div>
            </td>
          </tr>

          <tr class="total-row">
            <td class="print-date-cell">
              <div class="print-date">Итого</div>
            </td>

            <td v-for="meal in mealCards" :key="`total-${meal.meal}`" class="print-meal-cell">
              <div class="print-meal-summary">{{ weekTotals[meal.meal] }}</div>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import dayjs from 'dayjs'
import { message } from 'ant-design-vue'
import { LeftOutlined, RightOutlined } from '@ant-design/icons-vue'
import { getOrderByDate } from '../../api/orders'
import { label } from '../../utils/labels'

const loading = ref(false)
const dateValue = ref(dayjs())
const days = ref([])

const mealCards = [
  { meal: 'BREAKFAST', title: 'Завтрак' },
  { meal: 'LUNCH', title: 'Обед' },
  { meal: 'SNACK', title: 'Полдник' },
  { meal: 'DINNER', title: 'Ужин' },
]

const weekDates = computed(() => {
  const start = dayjs(dateValue.value).startOf('week')
  return Array.from({ length: 7 }, (_, index) => start.add(index, 'day'))
})

const weekLabel = computed(() => {
  const start = weekDates.value[0]
  const end = weekDates.value[6]
  return `${start.format('YYYY-MM-DD')} - ${end.format('YYYY-MM-DD')}`
})

const weekTotals = computed(() => {
  const totals = { BREAKFAST: 0, LUNCH: 0, SNACK: 0, DINNER: 0 }
  for (const item of days.value) {
    for (const meal of Object.keys(totals)) {
      totals[meal] += Number(item.meals[meal] || 0)
    }
  }
  return totals
})

const selectedDate = computed(() => dayjs(dateValue.value).format('YYYY-MM-DD'))

function shiftDate(daysShift) {
  dateValue.value = dayjs(dateValue.value).add(daysShift, 'day')
}

function tagToPrintIcon(tag) {
  const normalized = String(tag || '').trim().toLowerCase()
  if (['безглютеновая', 'без глютена', 'gluten free', 'gluten-free'].includes(normalized)) return '[БГ]'
  if (['безмолочная', 'без молока', 'dairy free', 'dairy-free'].includes(normalized)) return '[БМ]'
  if (['корректировка'].includes(normalized)) return '[К]'
  return ''
}

function normalizeOrderData(date, payload) {
  const data = payload?.data?.data || payload?.data || {}
  const order = data.order || {}

  return {
    date,
    exists: !!order.id,
    headerTitle: dayjs(date).format('YYYY-MM-DD, dd'),
    order: {
      id: order.id || null,
      status: order.status || null,
    },
    meals: {
      BREAKFAST: data.meals?.BREAKFAST || 0,
      LUNCH: data.meals?.LUNCH || 0,
      SNACK: data.meals?.SNACK || 0,
      DINNER: data.meals?.DINNER || 0,
    },
    children: {
      BREAKFAST: data.children?.BREAKFAST || [],
      LUNCH: data.children?.LUNCH || [],
      SNACK: data.children?.SNACK || [],
      DINNER: data.children?.DINNER || [],
    },
  }
}

async function load() {
  loading.value = true
  try {
    const responses = await Promise.all(
      weekDates.value.map(async date => {
        const dateStr = date.format('YYYY-MM-DD')
        const res = await getOrderByDate(dateStr)
        return normalizeOrderData(dateStr, res)
      })
    )
    days.value = responses
  } catch (e) {
    message.error(e?.response?.data?.error?.message || 'Не удалось загрузить недельный отчет')
  } finally {
    loading.value = false
  }
}

function printPage() {
  window.print()
}

watch(dateValue, () => {
  load()
})

load()
</script>

<style scoped>
.page-stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.sticky-toolbar {
  position: sticky;
  top: 16px;
  z-index: 20;
}

.sticky-toolbar-card :deep(.ant-card-body) {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.08);
}

.toolbar-label {
  font-weight: 600;
}

.toolbar-range {
  color: #666;
}

.print-sheet {
  width: 100%;
  background: #fff;
  color: #111;
}

.print-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 12px;
}

.print-header h1 {
  margin: 0;
  font-size: 24px;
}

.print-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  background: #fff;
}

.print-table th,
.print-table td {
  border: 1px solid #222;
  padding: 8px;
  vertical-align: top;
}

.print-table th {
  text-align: center;
  font-weight: 700;
}

.print-date-cell {
  width: 16%;
}

.print-date {
  font-weight: 700;
}

.print-status {
  margin-top: 4px;
  font-size: 12px;
  color: #666;
}

.print-meal-cell {
  width: 21%;
}

.print-meal-summary {
  font-weight: 700;
  margin-bottom: 6px;
}

.print-children {
  font-size: 10px;
}

.print-child {
  white-space: nowrap;
}

.print-tag-icons {
  margin-left: 4px;
  font-size: 9px;
}

.mobile-week-list {
  display: none;
}

.mobile-day-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  align-items: baseline;
}

.mobile-meal-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.mobile-meal-card {
  border: 1px solid #d9d9d9;
  border-radius: 10px;
  padding: 10px;
}

.mobile-meal-title {
  font-weight: 600;
  margin-bottom: 6px;
}

.selected-mobile-card {
  background: #eaf4ff;
}

.selected-row > td {
  background: #eaf4ff;
}

.total-row td {
  background: #f3f3f3;
}

@media (max-width: 767px) {
  .sticky-toolbar {
    top: 8px;
  }

  .mobile-week-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .print-table {
    display: none;
  }

  .print-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .mobile-meal-grid {
    grid-template-columns: 1fr;
  }
}

@media print {
  @page {
    margin: 6mm;
  }

  .print-controls {
    display: none !important;
  }
}
</style>
